import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { EntitiesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import useMapView from "@app/component/view/utils/useMapView";
import { enterGroupMode } from "@app/scene/map/store/groupMode/actions";
import { getLassoOptions, getRecords } from "@app/scene/map/store/selectors";
import LassoToolOptions from "@app/scene/map/types/LassoToolOptions";
import { isUpdating } from "@app/store/groups";
import { updateEntities } from "@app/store/groups/actions";
import { RootState } from "@app/store/rootReducer";
import loggingService from "@app/util/logging";

interface Props {
  lassoOptions: LassoToolOptions;
  onEnterGroupMode: typeof enterGroupMode;
  onExit?: () => void;
  selectedRecords: EntitiesSupportedByMapsPage[];
  updateEntities?: typeof updateEntities.request;
  updating?: boolean;
}

const AddToGroupTools: React.FC<Props> = ({
  lassoOptions,
  onEnterGroupMode,
  onExit,
  selectedRecords,
  updateEntities,
  updating,
}) => {
  const intl = useIntl();

  const { updateViewStateAndFetch } = useMapView();
  // TODO: check if this is a reliable way or we'd better move this reloading logic into sagas
  useEffect(() => {
    // reload map once when the mode is activated to only get records of a certain type
    updateViewStateAndFetch({ request: {} });
    return () => {
      // reload again when exiting the lasso mode
      updateViewStateAndFetch({ request: {} });
    };
  }, [updateViewStateAndFetch]);

  useEffect(() => {
    if (!lassoOptions.addItemsToGroupId) {
      loggingService.warning("AddToGroup lasso mode requires groupId, but none was given");
    }

    if (lassoOptions.visibleEntityTypes.length !== 1) {
      loggingService.warning(
        "AddToGroup lasso mode accepts only a single visible entity type, however these types are visible now:",
        lassoOptions.visibleEntityTypes
      );
    }
  }, [lassoOptions?.addItemsToGroupId, lassoOptions.visibleEntityTypes]);

  const handleClick = useCallback(() => {
    if (lassoOptions.visibleEntityTypes.length !== 1) {
      return;
    }

    const entityType = lassoOptions.visibleEntityTypes[0];
    const groupId = lassoOptions.addItemsToGroupId;
    if (groupId) {
      updateEntities?.({
        entities: selectedRecords,
        entityType,
        groupIdsToAdd: [groupId],
        onSuccess: () => {
          onEnterGroupMode({ entityType, groupId });
          onExit?.();
        },
      });
    }
  }, [
    lassoOptions?.addItemsToGroupId,
    lassoOptions.visibleEntityTypes,
    onEnterGroupMode,
    onExit,
    selectedRecords,
    updateEntities,
  ]);

  return (
    <div>
      <Button
        // TODO: uncomment the code below, now enabling purely for the demo
        disabled={updating /* || !selectedRecords.length */}
        loading={updating}
        onClick={handleClick}
        type="primary"
      >
        {intl.formatMessage({
          id: "map.tool.lasso.toolbar.addToGroup.addToGroupButton",
          defaultMessage: "Add to Group",
          description: "Add to Group button shown under LassoBar when adding pins to group",
        })}
      </Button>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  lassoOptions: getLassoOptions(state),
  selectedRecords: getRecords(state),
  updating: isUpdating(state),
});

const mapDispatchToProps = {
  onEnterGroupMode: enterGroupMode,
  updateEntities: updateEntities.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddToGroupTools);
